import { useCallback, useMemo, useState, useEffect } from 'react';
import { Field } from '@tw/tw-component-library';
import {
  useClientMatterSearch,
  FormattedClientData,
  FormattedMatterData,
} from './useClientMatterSearch';
import { useFormContext } from '../contexts/useFormContext';

// Extended field type to include clientMatter extras
interface FieldWithClientMatter extends Field {
  extras?: Field['extras'] & {
    clientMatter?: {
      // Configuration properties
      clientNameField?: string;
      matterNameField?: string;
      clientUnoField?: string;
      matterUnoField?: string;
      clientCodeField?: string;
      matterCodeField?: string;
      selectedClientLabelField?: string;
      selectedMatterLabelField?: string;
      office?: string;
      clientLabel?: string;
      matterLabel?: string;

      // Search functions
      onClientSearch?: (searchValue: string) => void;
      onMatterSearch?: (searchValue: string) => void;
      onClientSelect?: (selectedOption: string) => void;
      onMatterSelect?: (selectedOption: string) => void;

      // Data properties
      clientData?: string[];
      matterData?: string[];
      formattedClientData?: FormattedClientData[];
      formattedMatterData?: FormattedMatterData[];

      // Loading states
      isLoadingClients?: boolean;
      isLoadingMatters?: boolean;

      // Selected values
      selectedClientUno?: string;
      selectedMatterUno?: string;
      selectedClientLabel?: string;
    };
  };
}

export const useClientMatterOperations = (office?: string) => {
  // Use the existing client matter search hook

  const clientMatterSearch = useClientMatterSearch(office);
  const { updateFormData } = useFormContext();

  // Local state to maintain data across re-renders
  const [localClientData, setLocalClientData] = useState<string[]>([]);
  const [localMatterData, setLocalMatterData] = useState<string[]>([]);
  const [localFormattedClientData, setLocalFormattedClientData] = useState<
    FormattedClientData[]
  >([]);
  const [localFormattedMatterData, setLocalFormattedMatterData] = useState<
    FormattedMatterData[]
  >([]);
  const [localIsLoadingClients, setLocalIsLoadingClients] = useState(false);
  const [localIsLoadingMatters, setLocalIsLoadingMatters] = useState(false);

  // Update local state when clientMatterSearch state changes
  useEffect(() => {
    setLocalFormattedClientData(clientMatterSearch.formattedClientData);
    setLocalClientData(clientMatterSearch.clientData);

    setLocalFormattedMatterData(clientMatterSearch.formattedMatterData);
    setLocalMatterData(clientMatterSearch.matterData);

    setLocalIsLoadingClients(clientMatterSearch.isLoadingClients);
    setLocalIsLoadingMatters(clientMatterSearch.isLoadingMatters);
  }, [
    clientMatterSearch.clientData,
    clientMatterSearch.matterData,
    clientMatterSearch.formattedClientData,
    clientMatterSearch.formattedMatterData,
    clientMatterSearch.isLoadingClients,
    clientMatterSearch.isLoadingMatters,
  ]);

  // Process clientMatter field
  const processClientMatterField = useCallback(
    (field: FieldWithClientMatter) => {
      
      if (field.type === 'clientMatter') {
        const clientMatterConfig = field.extras?.clientMatter;

        const handleClientSelect = async (selectedOption: string) => {
          await clientMatterSearch.handleClientSelect(selectedOption);

          const selectedClient = localFormattedClientData.find(
            (client) => client.label === selectedOption
          );
          if (!selectedClient) return;

          const updates: Record<string, unknown> = {};

          if (clientMatterConfig?.clientNameField) {
            updates[clientMatterConfig.clientNameField] =
              selectedClient.clientName;
          }
          if (clientMatterConfig?.clientUnoField) {
            updates[clientMatterConfig.clientUnoField] = selectedClient.value;
          }
          if (clientMatterConfig?.clientCodeField) {
            updates[clientMatterConfig.clientCodeField] =
              selectedClient.clientCode;
          }
          if (clientMatterConfig?.selectedClientLabelField) {
            updates[clientMatterConfig.selectedClientLabelField] =
              selectedClient.label;
          }

          if (Object.keys(updates).length > 0) {
            updateFormData(updates);
          }
        };

        const handleMatterSelect = async (selectedOption: string) => {
          await clientMatterSearch.handleMatterSelect(selectedOption);

          const selectedMatter = localFormattedMatterData.find(
            (matter) => matter.label === selectedOption
          );
          if (!selectedMatter) return;

          const updates: Record<string, unknown> = {};

          if (clientMatterConfig?.matterNameField) {
            updates[clientMatterConfig.matterNameField] =
              selectedMatter.matterName;
          }
          if (clientMatterConfig?.matterUnoField) {
            updates[clientMatterConfig.matterUnoField] = selectedMatter.value;
          }
          if (clientMatterConfig?.matterCodeField) {
            updates[clientMatterConfig.matterCodeField] =
              selectedMatter.matterCode;
          }
          if (clientMatterConfig?.selectedMatterLabelField) {
            updates[clientMatterConfig.selectedMatterLabelField] =
              selectedMatter.label;
          }
          if (clientMatterConfig?.clientUnoField) {
            updates[clientMatterConfig.clientUnoField] = selectedMatter.clientUno;
          }

          if (Object.keys(updates).length > 0) {
            updateFormData(updates);
          }
        };

        const clientMatterProps = {
          // Pass through the search functions and data
          onClientSearch: clientMatterSearch.handleClientSearch,
          onMatterSearch: clientMatterSearch.handleMatterSearch,
          onClientSelect: handleClientSelect,
          onMatterSelect: handleMatterSelect,

          // Pass through the local state data
          clientData: localClientData,
          matterData: localMatterData,
          formattedClientData: localFormattedClientData,
          formattedMatterData: localFormattedMatterData,

          // Pass through loading states
          isLoadingClients: localIsLoadingClients,
          isLoadingMatters: localIsLoadingMatters,

          // Pass through selected values
          selectedClientUno: clientMatterSearch.selectedClientUno,
          selectedMatterUno: clientMatterSearch.selectedMatterUno,
          selectedClientLabel: clientMatterSearch.selectedClientLabel,
        };

        // Debug logging to see what data we're processing (only when data changes)
        if (
          localFormattedClientData.length > 0 ||
          localFormattedMatterData.length > 0
        ) {
          console.log(
            '🔍 useClientMatterOperations processing field with data:',
            {
              fieldName: field.name,
              clientData: localClientData.length,
              matterData: localMatterData.length,
              formattedClientData: localFormattedClientData.length,
              formattedMatterData: localFormattedMatterData.length,
              isLoadingClients: localIsLoadingClients,
              isLoadingMatters: localIsLoadingMatters,
            }
          );
        }

        const processedField = {
          ...field,
          extras: {
            ...field.extras,
            clientMatter: {
              ...field.extras?.clientMatter,
              ...clientMatterProps,
            },
          },
        };

        // Debug: Log what we're actually passing to the field
        console.log('🔍 useClientMatterOperations processed field extras:', {
          fieldName: field.name,
          hasClientMatter: !!processedField.extras?.clientMatter,
          clientMatterKeys: Object.keys(
            processedField.extras?.clientMatter || {}
          ),
          clientDataLength:
            processedField.extras?.clientMatter?.clientData?.length || 0,
          hasOnClientSearch:
            !!processedField.extras?.clientMatter?.onClientSearch,
        });

        return processedField;
      }
      return field;
    },
    [
      clientMatterSearch,
      updateFormData,
      // Include the local state data
      localClientData,
      localMatterData,
      localFormattedClientData,
      localFormattedMatterData,
      localIsLoadingClients,
      localIsLoadingMatters,
    ]
  );

  // Get clientMatter state for external use
  const clientMatterState = useMemo(
    () => ({
      clientData: localClientData,
      matterData: localMatterData,
      formattedClientData: localFormattedClientData,
      formattedMatterData: localFormattedMatterData,
      selectedClientUno: clientMatterSearch.selectedClientUno,
      selectedMatterUno: clientMatterSearch.selectedMatterUno,
      selectedClientLabel: clientMatterSearch.selectedClientLabel,
      isLoadingClients: localIsLoadingClients,
      isLoadingMatters: localIsLoadingMatters,
    }),
    [
      localClientData,
      localMatterData,
      localFormattedClientData,
      localFormattedMatterData,
      clientMatterSearch.selectedClientUno,
      clientMatterSearch.selectedMatterUno,
      clientMatterSearch.selectedClientLabel,
      localIsLoadingClients,
      localIsLoadingMatters,
    ]
  );

  return {
    processClientMatterField,
    clientMatterState,
  };
};
